"use client";

import { memo } from "react";
import {
  useAuth,
  useInterimStateManager,
  useSearchQueryBacklog,
  useUserChoices,
} from "@/state/globalStore";
import { MeiliOrigin } from "@/generated/fastapi";
import { SearchHitRendererProps } from "@/features/search";
import { useChatBot } from "@/features/chat";
import { OriginItem } from "@/features/origins";
import { PROFILE_SHAPING_LABELS } from "@/features/profile-shaping";
import { useBacklogOriginDrawers } from "../contexts/BacklogOriginDrawersProvider";
import { useBacklogContext } from "../contexts/BacklogContextProvider";

type BacklogOriginSearchHitProps = SearchHitRendererProps<MeiliOrigin> & {
  isInSearchContext?: boolean;
};

function BacklogOriginSearchHit({
  hit,
  config,
  isInSearchContext = true,
  onDeleteSearchResultAction,
  onUpdateSearchResultAction,
}: BacklogOriginSearchHitProps) {
  const { isUserAdmin } = useAuth();
  const { setInterimState, resetInterimState } = useInterimStateManager();
  const { searchQuery } = useSearchQueryBacklog();
  const { isSelectedOrigin, toggleSelectedOrigin, removeSelectedOrigins } =
    useUserChoices();
  const { handleSelectedOriginsDeleteClick } =
    useBacklogContext().originHandlers;
  const { handleChatWithProfileClick } = useChatBot();
  const { openExtractThoughtsDrawer, openIdentifyTopicsDrawer } =
    useBacklogOriginDrawers();

  const origin = hit as MeiliOrigin;
  function onDeleteOriginSuccess(deletedOriginId: string) {
    onDeleteSearchResultAction?.(deletedOriginId);
    removeSelectedOrigins([deletedOriginId]);
    resetInterimState("origin_detail");
    resetInterimState("edit_origin");
    resetInterimState("extend_origin");
  }

  return (
    <OriginItem
      hit={hit}
      config={config}
      isInSearchContext={isInSearchContext}
      surfaceVariant="backlog"
      onDeleteSearchResultAction={onDeleteOriginSuccess}
      onDeleteOrigin={handleSelectedOriginsDeleteClick}
      onOpenDetail={(detail) => {
        setInterimState("origin_detail", {
          isOpen: true,
          value: {
            ...detail,
            searchQuery: searchQuery.trim(),
          },
        });
      }}
      selection={{
        isSelected: isSelectedOrigin(origin.objectID),
        onToggle: () => toggleSelectedOrigin(origin.objectID),
        humanReadable: "Select source material for batch operations",
      }}
      primaryAction={{
        onClick: (selectedOrigin) =>
          openExtractThoughtsDrawer({
            originId: selectedOrigin.objectID,
            originContent: selectedOrigin.raw_text_input,
            language: selectedOrigin.language,
            onExtractionSuccess: () => {
              onUpdateSearchResultAction?.({
                ...origin,
                processed_status: "PROCESSED",
              });
            },
          }),
        icon: "extract",
        humanReadable: PROFILE_SHAPING_LABELS.backlogSourceCta,
        actionDescription: "Shape",
        color: "moveon.primary",
      }}
      secondaryAction={{
        onClick: (selectedOrigin) => {
          openIdentifyTopicsDrawer({
            originId: selectedOrigin.objectID,
            originContent: selectedOrigin.raw_text_input,
            language: selectedOrigin.language,
          });
        },
        icon: "identify_topics",
        humanReadable: PROFILE_SHAPING_LABELS.findTopics,
        hide: !isUserAdmin,
        actionDescription: "Topics",
      }}
      tertiaryAction={{
        onClick: (selectedOrigin) =>
          handleChatWithProfileClick(selectedOrigin.raw_text_input),
        icon: "chat",
        humanReadable: "Insert to chat",
        hide: !isUserAdmin,
        actionDescription: "Chat",
      }}
      editAction={{
        onClick: (selectedOrigin) => {
          setInterimState("edit_origin", {
            isOpen: true,
            value: {
              origin: selectedOrigin,
            },
          });
        },
        icon: "edit",
        humanReadable: "Edit source material",
        actionDescription: "Edit",
      }}
      extendAction={{
        onClick: (selectedOrigin) => {
          setInterimState("extend_origin", {
            isOpen: true,
            value: {
              origin: selectedOrigin,
            },
          });
        },
        icon: "plus",
        humanReadable: "Extend source material",
        actionDescription: "Extend",
      }}
    />
  );
}

export default memo(BacklogOriginSearchHit);
